import { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config';

/**
 * Fetches Meta App Review feature flags from backend once per session.
 * Cached in-memory. Returns { flags: {...}, isDemoOrg: bool, loading: bool }.
 *
 * Approved permissions unlock features publicly. Rejected/blocked
 * permissions keep features hidden or "Coming Soon" for real orgs
 * (but fully enabled for demo org 21 for re-recording videos).
 */

let cachedFlags = null;
let cachedIsDemoOrg = null;
let inFlightPromise = null;

async function fetchFlags() {
    if (cachedFlags) return { flags: cachedFlags, isDemoOrg: cachedIsDemoOrg };
    if (inFlightPromise) return inFlightPromise;

    inFlightPromise = (async () => {
        try {
            const token = localStorage.getItem('token');
            const r = await axios.get(`${config.API_BASE_URL}/api/users/feature-flags`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            cachedFlags = r.data?.data?.flags || {};
            cachedIsDemoOrg = r.data?.data?.isDemoOrg || false;
            return { flags: cachedFlags, isDemoOrg: cachedIsDemoOrg };
        } catch (e) {
            console.warn('[useFeatureFlags] fetch failed, defaulting to closed state:', e.message);
            cachedFlags = {};
            cachedIsDemoOrg = false;
            return { flags: {}, isDemoOrg: false };
        } finally {
            inFlightPromise = null;
        }
    })();

    return inFlightPromise;
}

export function clearFeatureFlagsCache() {
    cachedFlags = null;
    cachedIsDemoOrg = null;
    inFlightPromise = null;
}

/**
 * Is Ask Olli switched on for this server? The backend sends flags.ask_olli
 * (ASK_OLLI_ENABLED; off on production for now). Shown only on an explicit
 * true: if the flags call fails (an expired session, say) it stays hidden
 * rather than appearing on a server where it is off.
 */
export function useAskOlli() {
    const { flags, loading } = useFeatureFlags();
    return !loading && flags.ask_olli === true;
}

export default function useFeatureFlags() {
    const [state, setState] = useState({
        flags: cachedFlags || {},
        isDemoOrg: cachedIsDemoOrg || false,
        loading: !cachedFlags
    });

    useEffect(() => {
        let mounted = true;
        fetchFlags().then(({ flags, isDemoOrg }) => {
            if (mounted) setState({ flags, isDemoOrg, loading: false });
        });
        return () => { mounted = false; };
    }, []);

    return {
        ...state,
        isEnabled: (name) => state.flags[name] === true
    };
}
